import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/helpers';
import { CheckCircle2, Share2 } from 'lucide-react';

export const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const [username, setUsername] = useState(user?.username || '');
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [dmOnlyMutuals, setDmOnlyMutuals] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setName(user.name || '');
      setBio(user.bio || '');
      setWalletAddress(user.public_wallet_address || '');
      setDefaultCurrency(user.default_currency || '');
      setDmOnlyMutuals(!!user.dm_only_mutuals);
    }
  }, [user]);

  useEffect(() => {
    publicAPI.getCurrencies(true).then(res => setCurrencies(res.data)).catch(() => {});
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.updateProfile({
        username,
        name,
        bio,
        public_wallet_address: walletAddress,
        default_currency: defaultCurrency || null,
        dm_only_mutuals: dmOnlyMutuals,
      });
      toast.success('Profile updated');
      checkAuth();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Update failed');
    } finally { setLoading(false); }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) { toast.error('Both password fields required'); return; }
    setLoading(true);
    try {
      await userAPI.updateProfile({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Update failed');
    } finally { setLoading(false); }
  };

  const copyProfileLink = async () => {
    if (!user?.username) { toast.error('Set a username first'); return; }
    const url = `${window.location.origin}/@${user.username}`;
    await copyToClipboard(url);
    toast.success('Profile link copied!');
  };

  return (
    <FocusedLayout title="Profile & Settings">
      <div className="space-y-6 max-w-2xl" data-testid="profile-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile</h1>
              {user?.verified && (
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 text-xs font-medium">
                  <CheckCircle2 className="h-3 w-3" /> Verified
                </div>
              )}
            </div>
            <p className="mt-2 text-muted-foreground">Manage your account and public identity</p>
          </div>
          {user?.username && (
            <div className="flex gap-2">
              <Link to={`/@${user.username}`}>
                <Button variant="outline" size="sm">View Public Profile</Button>
              </Link>
              <Button variant="outline" size="sm" onClick={copyProfileLink} data-testid="copy-my-profile-link">
                <Share2 className="h-3 w-3 mr-1" /> Copy Link
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardHeader><CardTitle>Public Identity</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="h-11" />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label>Username (your @handle)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">@</span>
                  <Input value={username} onChange={(e) => setUsername(e.target.value)} className="h-11" data-testid="profile-username-input" placeholder="susan" />
                </div>
                <p className="text-xs text-muted-foreground">Others can send you funds using @{username || 'yourhandle'}</p>
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell others about yourself..." rows={3} data-testid="profile-bio-input" />
              </div>
              <div className="space-y-2">
                <Label>Public Wallet Address (Optional)</Label>
                <Input value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} placeholder="External wallet address for receiving crypto" className="h-11 font-mono text-sm" data-testid="profile-wallet-input" />
                <p className="text-xs text-muted-foreground">Shown on your public profile for people to send you external funds</p>
              </div>
              <div className="space-y-2">
                <Label>Default Display Currency</Label>
                <Select value={defaultCurrency || 'none'} onValueChange={(v) => setDefaultCurrency(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-11" data-testid="default-currency-select">
                    <SelectValue placeholder="Choose a currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default (show each currency natively)</SelectItem>
                    {currencies.map(c => (
                      <SelectItem key={c.symbol} value={c.symbol}>
                        {c.symbol} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Your dashboard will show all wallet balances converted into this currency (e.g. 1 KCRD ≈ 1,000 ZAR) using current exchange rates.
                </p>
              </div>
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-border" data-testid="dm-privacy-toggle-row">
                <div className="space-y-1">
                  <Label className="cursor-pointer">Only accept messages from mutual followers</Label>
                  <p className="text-xs text-muted-foreground">When on, only users you follow back can start new conversations with you.</p>
                </div>
                <Switch
                  checked={dmOnlyMutuals}
                  onCheckedChange={setDmOnlyMutuals}
                  data-testid="dm-only-mutuals-switch"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="save-profile-button">
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Change Password</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Current Password</Label>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="h-11" data-testid="current-password-input" />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-11" data-testid="new-password-input" />
              </div>
              <Button type="submit" disabled={loading} data-testid="save-password-button">
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </FocusedLayout>
  );
};
